import * as React from "react"

const MOBILE_BREAKPOINT = 768

interface UseIsMobileOptions {
  containerRef?: React.RefObject<HTMLElement | null>
  breakpoint?: number
}

export function useIsMobile({
  containerRef,
  breakpoint = MOBILE_BREAKPOINT,
}: UseIsMobileOptions = {}) {
  const [isMobile, setIsMobile] = React.useState(false)

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const setFromWidth = (width: number) => {
      setIsMobile(width < breakpoint)
    }

    if (!containerRef) {
      const updateFromViewport = () => {
        setFromWidth(window.innerWidth)
      }

      updateFromViewport()
      window.addEventListener("resize", updateFromViewport)

      return () => {
        window.removeEventListener("resize", updateFromViewport)
      }
    }

    let frameId = 0
    let resizeObserver: ResizeObserver | null = null

    const observeContainer = () => {
      const container = containerRef.current
      if (!container) {
        frameId = window.requestAnimationFrame(observeContainer)
        return
      }

      setFromWidth(container.getBoundingClientRect().width)

      resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (entry) {
          setFromWidth(entry.contentRect.width)
        }
      })

      resizeObserver.observe(container)
    }

    observeContainer()

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId)
      }
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [containerRef, breakpoint])

  return isMobile
}
